/* Admin Availability tab — slot-aware date blocking.
   Two cruise time-slots per day:
     • Day     · 12pm–4pm
     • Evening · 6pm–10pm
   Admin can block one slot, both, or the whole day.
   Shift-click for range select. Click-toggle for individual dates.
*/
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Save, Trash2, X, Sun, Moon, Ban } from "lucide-react";
import { adminApi } from "./adminApi";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAY_LABELS = ["S","M","T","W","T","F","S"];

const SLOT_META = {
 day:     { label: "Day",     time: "12–4pm", icon: Sun,  dot: "bg-amber-300",  ring: "ring-amber-400" },
 evening: { label: "Evening", time: "6–10pm", icon: Moon, dot: "bg-indigo-400", ring: "ring-indigo-400" },
 all:     { label: "All-day", time: "Both cruises", icon: Ban,  dot: "bg-rose-400",   ring: "ring-rose-400" },
};

const toISO = (date) => {
 const y = date.getFullYear();
 const m = String(date.getMonth() + 1).padStart(2, "0");
 const d = String(date.getDate()).padStart(2, "0");
 return `${y}-${m}-${d}`;
};

const buildGrid = (year, month) => {
 const first = new Date(year, month, 1);
 const startDow = first.getDay();
 const daysInMonth = new Date(year, month + 1, 0).getDate();
 const cells = [];
 for (let i = 0; i < startDow; i++) cells.push(null);
 for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
 while (cells.length % 7 !== 0) cells.push(null);
 return cells;
};

/** Returns which slots are blocked for a given ISO date.
 *  Result: { day: bool, evening: bool, all: bool } */
const slotsFor = (iso, blocks) => {
 const entry = blocks.get(iso);
 if (!entry) return { day: false, evening: false, all: false };
 return { day: !!entry.day, evening: !!entry.evening, all: !!entry.all };
};

export default function AdminAvailability({ onUnauthorized, showToast }) {
 const today = useMemo(() => { const t = new Date(); t.setHours(0,0,0,0); return t; }, []);
 const [cursor, setCursor] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
 // Map<isoDate, {day: bool, evening: bool, all: bool}>
 const [blocks, setBlocks] = useState(new Map());
 const [selected, setSelected] = useState(new Set());
 const [lastClicked, setLastClicked] = useState(null);
 const [slot, setSlot] = useState("all"); // day | evening | all
 const [loading, setLoading] = useState(true);
 const [saving, setSaving] = useState(false);

 const load = useCallback(async () => {
  setLoading(true);
  try {
   const res = await adminApi("/admin/blocked-dates");
   const list = (res && res.blocks) ? res.blocks : (res && res.dates ? res.dates : []);
   const map = new Map();
   for (const b of list) {
    const iso = b.date;
    const s = (b.slot || "all").toLowerCase();
    if (!iso) continue;
    const prev = map.get(iso) || { day: false, evening: false, all: false };
    if (s === "all") prev.all = true;
    else if (s === "day") prev.day = true;
    else if (s === "evening") prev.evening = true;
    map.set(iso, prev);
   }
   setBlocks(map);
  } catch (e) {
   if (e.status === 401) { onUnauthorized(); return; }
   showToast(e.message || "Failed to load", "err");
  } finally { setLoading(false); }
 }, [onUnauthorized, showToast]);

 useEffect(() => { load(); }, [load]);

 const grid = useMemo(() => buildGrid(cursor.getFullYear(), cursor.getMonth()), [cursor]);
 const goPrev = () => {
  const next = new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1);
  const limit = new Date(today.getFullYear(), today.getMonth(), 1);
  if (next < limit) return;
  setCursor(next);
 };
 const goNext = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));

 const toggleSelect = (date, shiftKey) => {
  const iso = toISO(date);
  const next = new Set(selected);
  if (shiftKey && lastClicked) {
   const a = new Date(lastClicked + "T00:00:00");
   const b = date;
   const start = a < b ? a : b;
   const end = a < b ? b : a;
   const cur = new Date(start);
   while (cur <= end) {
    if (cur >= today) next.add(toISO(cur));
    cur.setDate(cur.getDate() + 1);
   }
  } else {
   if (next.has(iso)) next.delete(iso); else next.add(iso);
  }
  setSelected(next);
  setLastClicked(iso);
 };

 const clearSelection = () => { setSelected(new Set()); setLastClicked(null); };
 const selectAllVisible = () => {
  const next = new Set(selected);
  for (const d of grid) if (d && d >= today) next.add(toISO(d));
  setSelected(next);
 };

 const apply = async (mode) => {
  if (selected.size === 0) return;
  setSaving(true);
  try {
   const dates = Array.from(selected);
   await adminApi("/admin/blocked-dates", {
    method: mode === "block" ? "POST" : "DELETE",
    body: { dates, slot },
   });
   await load(); // refetch to reflect merges/splits computed on the server
   clearSelection();
   const slotLabel = SLOT_META[slot].label;
   showToast(`${dates.length} date${dates.length === 1 ? "" : "s"} · ${slotLabel} ${mode === "block" ? "blocked" : "unblocked"}.`);
  } catch (e) {
   if (e.status === 401) { onUnauthorized(); return; }
   showToast(e.message || `${mode} failed`, "err");
  } finally { setSaving(false); }
 };

 // Totals for the summary chip strip
 const summary = useMemo(() => {
  let all = 0, day = 0, evening = 0;
  for (const [, v] of blocks) {
   if (v.all) all++;
   else {
    if (v.day) day++;
    if (v.evening) evening++;
   }
  }
  return { all, day, evening, total: blocks.size };
 }, [blocks]);

 return (
  <div data-testid="admin-tab-availability">
   {/* Slot picker */}
   <div className="mb-4 rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid="admin-slot-picker">
    <div className="text-[10px] uppercase tracking-[0.28em] font-black text-white/55 mb-2.5">Which cruise slot to block?</div>
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
     {(["day", "evening", "all"]).map((s) => {
      const meta = SLOT_META[s];
      const Icon = meta.icon;
      const active = slot === s;
      return (
       <button
        key={s}
        type="button"
        onClick={() => setSlot(s)}
        className={`flex items-center gap-3 rounded-xl border px-4 py-3 transition-all text-left ${
         active
          ? `bg-white/[0.09] border-white/30 ring-2 ${meta.ring} shadow-lg`
          : "bg-white/[0.02] border-white/10 hover:bg-white/[0.06]"
        }`}
        data-testid={`admin-slot-btn-${s}`}
       >
        <span className={`w-8 h-8 rounded-full flex items-center justify-center ${active ? meta.dot + " text-[#0A0A0A]" : "bg-white/[0.06] text-white/70"}`}>
         <Icon className="w-4 h-4" />
        </span>
        <span className="min-w-0">
         <div className="text-[13px] font-black text-white leading-tight">{meta.label}</div>
         <div className="text-[10.5px] uppercase tracking-[0.14em] text-white/55 mt-0.5">{meta.time}</div>
        </span>
       </button>
      );
     })}
    </div>
    <p className="mt-3 text-[11px] text-white/50 leading-relaxed">
     Cruises normally run <strong className="text-white/70">Day (12–4pm)</strong> and <strong className="text-white/70">Evening (6–10pm)</strong>.
     Block one, the other, or the whole day. When you block both slots on a date it collapses into "All-day".
    </p>
   </div>

   <div className="rounded-3xl border border-white/10 bg-white/[0.02] overflow-hidden">
    <div className="flex items-center justify-between gap-3 px-5 lg:px-7 py-5 border-b border-white/10">
     <button onClick={goPrev} className="w-10 h-10 rounded-full bg-white/[0.06] hover:bg-white/[0.12] flex items-center justify-center transition-colors" data-testid="admin-cal-prev"><ChevronLeft className="w-4 h-4" /></button>
     <div className="text-xl lg:text-2xl font-black tracking-tight" data-testid="admin-cal-month">{MONTHS[cursor.getMonth()]} {cursor.getFullYear()}</div>
     <button onClick={goNext} className="w-10 h-10 rounded-full bg-white/[0.06] hover:bg-white/[0.12] flex items-center justify-center transition-colors" data-testid="admin-cal-next"><ChevronRight className="w-4 h-4" /></button>
    </div>
    <div className="p-5 lg:p-8">
     <div className="grid grid-cols-7 gap-1.5 mb-2">
      {DAY_LABELS.map((d, i) => <div key={i} className="text-center text-[10px] uppercase tracking-wider font-bold text-white/45 py-1">{d}</div>)}
     </div>
     <div className="grid grid-cols-7 gap-1.5" data-testid="admin-cal-grid">
      {grid.map((date, idx) => {
       if (!date) return <div key={idx} className="aspect-square" />;
       const iso = toISO(date);
       const past = date < today;
       const s = slotsFor(iso, blocks);
       const isSelected = selected.has(iso);
       const isFullyBlocked = s.all;
       const isPartial = !s.all && (s.day || s.evening);

       let cls = "bg-white/[0.04] hover:bg-white/[0.10] text-white/85";
       if (past) cls = "bg-white/[0.02] text-white/25 cursor-not-allowed line-through";
       else if (isFullyBlocked && isSelected) cls = "bg-rose-500/40 ring-2 ring-rose-300 text-white";
       else if (isFullyBlocked) cls = "bg-rose-500/25 ring-1 ring-rose-400/60 text-white";
       else if (isPartial && isSelected) cls = "bg-amber-400/35 ring-2 ring-amber-300 text-white";
       else if (isPartial) cls = "bg-amber-400/15 ring-1 ring-amber-400/50 text-white/90";
       else if (isSelected) cls = "bg-amber-400/30 ring-2 ring-amber-300 text-white";

       return (
        <button
         key={idx}
         type="button"
         disabled={past}
         onClick={(e) => !past && toggleSelect(date, e.shiftKey)}
         className={`relative aspect-square rounded-xl flex flex-col items-center justify-center text-sm font-bold transition-all ${cls}`}
         data-testid={`admin-cal-day-${iso}`}
         aria-pressed={isSelected}
        >
         <span>{date.getDate()}</span>

         {/* Slot dots at top of cell */}
         {!past && !isFullyBlocked && (s.day || s.evening) && (
          <div className="absolute top-1.5 left-1/2 -translate-x-1/2 flex items-center gap-1" data-testid={`admin-cal-slots-${iso}`}>
           {s.day && <span className="w-1.5 h-1.5 rounded-full bg-amber-300 shadow-sm" title="Day blocked" />}
           {s.evening && <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 shadow-sm" title="Evening blocked" />}
          </div>
         )}

         {/* Bottom label */}
         {!past && isFullyBlocked && (
          <span className="absolute bottom-1.5 text-[8px] uppercase tracking-[0.14em] font-black text-rose-200">Blocked</span>
         )}
         {!past && isPartial && (
          <span className="absolute bottom-1.5 text-[8px] uppercase tracking-[0.14em] font-black text-amber-200">
           {s.day ? "Day" : "Eve"}
          </span>
         )}
        </button>
       );
      })}
     </div>
     <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] text-white/65">
      <div className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-white/10 border border-white/15" /> Available</div>
      <div className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-amber-400/40 ring-1 ring-amber-400/60" /> One slot blocked</div>
      <div className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-rose-500/40 ring-1 ring-rose-300/60" /> All-day blocked</div>
      <div className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-amber-400/40 ring-1 ring-amber-300/60" /> Selected</div>
      <div className="text-white/35">Shift-click to select a range</div>
     </div>
    </div>
   </div>

   {/* Bulk action bar */}
   <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-4 flex flex-wrap items-center justify-between gap-3" data-testid="admin-bulk-bar">
    <div className="flex items-center gap-3">
     <CalendarIcon className="w-4 h-4 text-amber-300" />
     <span className="text-sm font-bold">{selected.size} date{selected.size === 1 ? "" : "s"} selected</span>
     <span className="hidden sm:inline text-[11px] uppercase tracking-[0.18em] font-black text-white/55">
      Slot: <span className="text-white/90">{SLOT_META[slot].label} ({SLOT_META[slot].time})</span>
     </span>
     {selected.size > 0 && <button onClick={clearSelection} className="inline-flex items-center gap-1 text-white/55 hover:text-white text-[11px] uppercase tracking-[0.18em] font-bold" data-testid="admin-clear-selection"><X className="w-3 h-3" /> Clear</button>}
    </div>
    <div className="flex items-center gap-2 flex-wrap">
     <button onClick={selectAllVisible} className="px-4 py-2 rounded-full border border-white/15 hover:bg-white/[0.06] text-white/85 text-[11px] uppercase tracking-[0.18em] font-bold transition-colors" data-testid="admin-select-all-month">Select whole month</button>
     <button onClick={() => apply("unblock")} disabled={selected.size === 0 || saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-emerald-400/50 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-200 text-[11px] uppercase tracking-[0.18em] font-black disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-testid="admin-unblock-btn"><Trash2 className="w-3.5 h-3.5" /> Unblock {SLOT_META[slot].label} {selected.size > 0 ? `(${selected.size})` : ""}</button>
     <button onClick={() => apply("block")} disabled={selected.size === 0 || saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-rose-400 to-rose-600 hover:from-rose-300 hover:to-rose-500 text-white text-[11px] uppercase tracking-[0.18em] font-black disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-testid="admin-block-btn"><Save className="w-3.5 h-3.5" /> Block {SLOT_META[slot].label} {selected.size > 0 ? `(${selected.size})` : ""}</button>
    </div>
   </div>

   {/* Blocked summary */}
   <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.02] px-5 py-4" data-testid="admin-blocked-summary">
    <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
     <div className="text-[10px] uppercase tracking-[0.28em] font-black text-white/55">
      All blocked dates ({summary.total})
     </div>
     <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.16em] font-black text-white/60">
      {summary.all > 0 && <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-rose-400" /> {summary.all} all-day</span>}
      {summary.day > 0 && <span className="inline-flex items-center gap-1.5"><Sun className="w-3 h-3 text-amber-300" /> {summary.day} day</span>}
      {summary.evening > 0 && <span className="inline-flex items-center gap-1.5"><Moon className="w-3 h-3 text-indigo-300" /> {summary.evening} evening</span>}
      {loading && <span className="text-white/45 normal-case tracking-normal">Loading…</span>}
     </div>
    </div>
    {summary.total === 0 ? (
     <div className="text-sm text-white/45">No dates blocked. Every future date and slot is available on the public calendar.</div>
    ) : (
     <div className="flex flex-wrap gap-2">
      {Array.from(blocks.entries()).sort(([a], [b]) => a.localeCompare(b)).map(([d, v]) => {
       const cls = v.all
        ? "bg-rose-500/15 border-rose-400/40 text-rose-100"
        : v.day && !v.evening ? "bg-amber-400/15 border-amber-300/40 text-amber-100"
        : "bg-indigo-500/15 border-indigo-400/40 text-indigo-100";
       const tag = v.all ? "All-day" : v.day && v.evening ? "Both" : v.day ? "Day" : "Evening";
       return (
        <span key={d} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-bold tabular-nums ${cls}`} data-testid={`admin-blocked-chip-${d}`}>
         {d}
         <span className="text-[9px] uppercase tracking-[0.16em] opacity-80">{tag}</span>
        </span>
       );
      })}
     </div>
    )}
   </div>
  </div>
 );
}
